import { format } from 'date-fns';

// Safe date formatter that handles null values
const safeFormatDate = (date: Date | string | null, formatStr: string, fallback: string = ''): string => {
  if (!date) return fallback;
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, formatStr);
  } catch (error) {
    console.error("Error formatting date:", error);
    return fallback;
  }
};
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { ADD_CAMPAIGN_CART } from "@/Graphql/packageQurie";
import { MapPin, Info, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useCart, type CartItem } from "@/utils/cartContext";
import { useEffect } from 'react';

interface PackageCardProps {
  package: any[];
  useLocalCart?: boolean;
  currencyCode?: string;
  onDeleteFromCart?: (pkg: any) => void;
  isAuthenticated?: boolean; // New prop to know if user is authenticated
  viewMode?: "grid" | "list" | "map"; // <-- Added viewMode prop
  campaignStartDate?: Date | null;
  campaignEndDate?: Date | null;
}

export default function PackageCard({ package: pkgArr, useLocalCart = false, currencyCode = '', onDeleteFromCart, isAuthenticated = false, viewMode = "grid", campaignStartDate, campaignEndDate }: PackageCardProps) {
  const { addToCart, removeFromCart, isInCart } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [addCampaignCart, { loading: addCartLoading }] = useMutation(ADD_CAMPAIGN_CART);
  
  // Log country information when component loads (for debugging)
  useEffect(() => {
    if (useLocalCart) {
      const selectedCountryId = localStorage.getItem('selectedCountryId') || localStorage.getItem('sessionDefaultCountryId');
      const selectedCountryName = localStorage.getItem('selectedCountryName') || localStorage.getItem('sessionDefaultCountryName');
      const cartCountry = localStorage.getItem('cartCountry');
      
      console.log('[PACKAGE-CARD] Component loaded with country info:', {
        useLocalCart,
        isAuthenticated,
        selectedCountryId,
        selectedCountryName,
        cartCountry,
        packageCount: pkgArr?.length || 0
      });
    }
  }, [useLocalCart, isAuthenticated, pkgArr]);
  
  if (!Array.isArray(pkgArr)) return null;

  // Debug log for incoming package array
  console.log('[PackageCard] pkgArr:', pkgArr);
  console.log('[PackageCard] viewMode:', viewMode); // Log viewMode for demonstration

  // Helper function to check if package is available based on campaign dates
  const isPackageAvailable = (pkg: any): boolean => {
    if (!campaignStartDate || !campaignEndDate) return true; // If no campaign dates, assume available

    const validityStart = pkg.startDate ? new Date(pkg.startDate) : null;
    const validityEnd = pkg.endDate ? new Date(pkg.endDate) : null;
    console.log("packageAvailable ", "validity start:", validityStart, "validityEnd:", validityEnd);

    if (!validityStart || !validityEnd) return true; // If no validity dates, assume available

    // Package is available if its validity period overlaps with or is within campaign period
    return validityStart <= campaignEndDate && validityEnd >= campaignStartDate;
  };

  const handleAddToCart = async (pkg: any) => {
    if (useLocalCart) {
      // Get country information from localStorage (for conflict detection)
      const selectedCountryId = localStorage.getItem('selectedCountryId') || localStorage.getItem('sessionDefaultCountryId');
      const selectedCountryName = localStorage.getItem('selectedCountryName') || localStorage.getItem('sessionDefaultCountryName') || pkg.country?.name || 'N/A';
      
      console.log('[PACKAGE-CARD] Adding package to cart (not logged in):', {
        packageId: pkg.id,
        packageName: pkg.name,
        countryId: selectedCountryId,
        countryName: selectedCountryName,
        pkgCountry: pkg.country?.name,
        useLocalCart: true
      });

      // Validate country conflict before adding
      const existingCartCountry = localStorage.getItem('cartCountry');
      
      if (existingCartCountry && existingCartCountry.toLowerCase() !== selectedCountryName.toLowerCase()) {
        toast({
          title: "Country Conflict",
          description: `Your cart contains items from ${existingCartCountry}. Cannot add package from ${selectedCountryName}.`,
          variant: "destructive"
        });
        return;
      }
      
      // Set cart country if not already set
      if (!existingCartCountry && selectedCountryName && selectedCountryName !== 'N/A') {
        localStorage.setItem('cartCountry', selectedCountryName);
        if (selectedCountryId) {
          localStorage.setItem('selectedCountryId', selectedCountryId);
          localStorage.setItem('cartCountryId', selectedCountryId);
        }
        console.log('[PACKAGE-CARD] Set cart country:', selectedCountryName);
      }

      // Use local cart context only (for explore page)
      const cartItem: CartItem = {
        id: pkg.id,
        name: pkg.name,
        location: pkg.country?.name || '',
        state: '',
        price: (pkg.totalPrice || 0) * 100, // Convert to cents for cart context
        impressions: pkg.estimation?.potentialViews || 0,
        size: (pkg.inventory?.total || '').toString(),
        type: 'Package',
        status: (pkg.numberOfPurchase < pkg.purchaseLimitation) ? 'available' : 'sold-out',
        image: pkg.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
        spots: undefined,
        campaignDuration: pkg.campaignDuration || undefined,
        dailyHours: undefined,
        isDeal: false,
        daypartSchedules: undefined,
        packageStartDate: pkg.startDate,
        packageEndDate: pkg.endDate,
        requiresCampaignDuration: false,
        // Store full package details for API synchronization after login
        packageDetails: pkg,
        currencyCode: pkg?.currency || currencyCode || pkg?.price?.localCurrencies?.code || 'N/A',
        specifications: {
          resolution: 'Various',
          duration: `${safeFormatDate(pkg.startDate ? new Date(pkg.startDate) : null, 'dd MMMM yyyy', '-')} - ${safeFormatDate(pkg.endDate ? new Date(pkg.endDate) : null, 'dd MMMM yyyy', '-')}`
        },
        uniqueVisitors: pkg.estimation?.uniqueReach || 0,
        frequency: pkg.estimation?.avgFrequency || 0,
        priceFormat: pkg?.currency || currencyCode || pkg?.price?.localCurrencies?.code || 'N/A',
        // Country fields for conflict detection
        countryId: selectedCountryId || undefined,
        countryName: selectedCountryName
      };

      console.log('[PACKAGE-CARD] Cart item with country fields:', {
        id: cartItem.id,
        name: cartItem.name,
        countryId: cartItem.countryId,
        countryName: cartItem.countryName,
        fullItem: cartItem
      });

      addToCart(cartItem);
      toast({
        title: 'Added to cart',
        description: `${pkg.name} added to cart successfully!`,
        variant: 'default',
      });
      return;
    }

    // Use API calls (for authenticated pages like start-campaign)
    // Helper to deep remove __typename recursively
    const removeTypename = (obj: any): any => {
      if (Array.isArray(obj)) return obj.map(removeTypename);
      if (obj && typeof obj === 'object') {
        const newObj: any = {};
        for (const key in obj) {
          if (key !== '__typename') {
            newObj[key] = removeTypename(obj[key]);
          }
        }
        return newObj;
      }
      return obj;
    };

    // Build input for mutation as per sample, but remove __typename from all nested objects
    const input = [{
      accessToken: localStorage.getItem('authToken') || '',
      userId: localStorage.getItem('userId') || '',
      campaignName: pkg.name || '',
      startDate: pkg.validityStartDate || pkg.startDate || '',
      endDate: pkg.validityEndDate || pkg.endDate || '',
      cartItemName: pkg.name || '',
      cartItemId: pkg.id || '',
      cartItemReferenceId: null,
      cartItemType: 'PACKAGE',
      cartItemCountry: pkg.country?.name || '',
      cartItemVenueType: null,
      cartItemResolution: '',
      cartItemThumbnailUrl: pkg.thumbnail || '',
      cartItemLatitude: null,
      cartItemLongitude: null,
      packageDetails: {
        ...removeTypename({
          id: pkg.id || '',
          name: pkg.name || '',
          description: pkg.description || '',
          country: pkg.country ? {
            id: pkg.country.id || '',
            name: pkg.country.name || '',
            timezone: pkg.country.timezone || ''
          } : null,
          publisher: pkg.publisher ? {
            id: pkg.publisher.id || '',
            name: pkg.publisher.name || ''
          } : null,
          startDate: pkg.startDate || '',
          endDate: pkg.endDate || '',
          validityStartDate: pkg.validityStartDate || '',
          validityEndDate: pkg.validityEndDate || '',
          registryPackageType: pkg.registryPackageType || '',
          campaignDuration: pkg.campaignDuration || 0,
          purchaseLimitation: pkg.purchaseLimitation || 0,
          numberOfPurchase: pkg.numberOfPurchase || 0,
          inventory: pkg.inventory || {
            type: [],
            total: 0,
            classic: 0,
            digital: 0
          },
          exposure: pkg.exposure || [],
          currency: pkg.currency || '',
          price: pkg.price || 0,
          discount: pkg.discount || 0,
          totalPrice: pkg.totalPrice || 0,
          thumbnail: pkg.thumbnail || '',
          estimation: pkg.estimation || {
            potentialViews: 0,
            uniqueReach: 0,
            avgFrequency: 0,
            cpm: 0,
            spotsPerHour: 0,
            totalSpots: 0
          },
          inventories: pkg.inventories || [],
          publishTo: pkg.publishTo || [],
          tagIds: [],
          tags: pkg.tags || [],
          reopened: pkg.reopened || false,
          createdBy: pkg.createdBy || '',
          createdDate: pkg.createdDate || '',
          lastModifiedBy: pkg.lastModifiedBy || '',
          lastModifiedDate: pkg.lastModifiedDate || '',
          discountPrice: pkg.discountPrice || {
            originalPrice: 0,
            totalPrice: 0,
            discountPrice: 0
          },
          loadingicon: pkg.loadingicon || false,
          cartAdded: pkg.cartAdded || false,
          displayCount: pkg.displayCount || 0,
          showlocation: pkg.showlocation || false,
          packageexpire: pkg.packageexpire || 0,
          packagescreens: pkg.packagescreens || 0,
          packagediscountPrice: pkg.packagediscountPrice || 0,
          type: 'PACKAGE'
        })
      },
      discount: pkg.discount ? (pkg.price * pkg.discount) / 100 : 0,
      nowPayPrice: pkg.totalPrice || 0,
      totalPrice: pkg.price || 0,
      spotDuration: null
    }];
    try {
      const { data } = await addCampaignCart({ variables: { input } });
      const itemDataArr = data?.addCampaignCart?.itemData;
      const itemData = Array.isArray(itemDataArr) ? itemDataArr[0] : undefined;
      if (itemData && itemData.success) {
        toast({
          title: 'Added to cart',
          description: `${pkg.name} added to cart successfully!`,
          variant: 'default',
        });
        // Optionally, add to local cart context for UI update
        addToCart({
          id: pkg.id,
          name: pkg.name,
          location: pkg.country?.name || '',
          state: '',
          price: pkg.totalPrice || 0,
          impressions: pkg.estimation?.potentialViews || 0,
          size: (pkg.inventory?.total || '').toString(),
          type: 'Package',
          status: (pkg.numberOfPurchase < pkg.purchaseLimitation) ? 'available' : 'sold-out',
          image: pkg.thumbnail || '',
          spots: undefined,
          campaignDuration: pkg.campaignDuration || undefined,
          dailyHours: undefined,
          isDeal: false,
          daypartSchedules: undefined,
          packageStartDate: pkg.startDate,
          packageEndDate: pkg.endDate,
          requiresCampaignDuration: false,
          specifications: {
            resolution: 'Various',
            duration: `${safeFormatDate(pkg.startDate ? new Date(pkg.startDate) : null, 'dd MMMM yyyy', '-')} - ${safeFormatDate(pkg.endDate ? new Date(pkg.endDate) : null, 'dd MMMM yyyy', '-')}`
          },
          uniqueVisitors: pkg.estimation?.uniqueReach || 0,
          frequency: pkg.estimation?.avgFrequency || 0,
          priceFormat: pkg?.currency || currencyCode || pkg?.price?.localCurrencies?.code || 'N/A'
        });
      } else {
        const errorMessage = (itemData && itemData.message) || 'Failed to add to cart.';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'An unexpected error occurred while adding to cart.',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveFromCart = (pkg: any) => {
    if (onDeleteFromCart) {
      // Use API-based deletion for authenticated users (start-campaign page)
      console.log('[PACKAGE DELETE] Using API deletion for package:', pkg.id);
      onDeleteFromCart(pkg);
    } else {
      // Use local cart context for unauthenticated users (explore page)
      console.log('[PACKAGE DELETE] Using local cart deletion for package:', pkg.id);
    removeFromCart(pkg.id);
    toast({
      title: 'Removed from cart',
      description: `${pkg.name} removed from cart successfully!`,
      variant: 'default',
    });
    }
  };

  const handleViewDetails = (inventoryItem: any) => {
    console.log("Viewing details for inventory item:", inventoryItem);

    const billboardType = inventoryItem.registryPackageType || inventoryItem.inventoryType || inventoryItem.type || 'digital';
    // Use campaign dates instead of package validity dates
    const startDate = campaignStartDate ? campaignStartDate.toISOString() : '';
    const endDate = campaignEndDate ? campaignEndDate.toISOString() : '';
    const campaignName = inventoryItem.name || '';
    navigate(`/package/${inventoryItem.id}?type=${encodeURIComponent(billboardType)}`, {
      state: {
        currentPrice: inventoryItem.totalPrice || inventoryItem.price || 0,
        startDate,
        endDate,
        campaignName,
        currencyCode: inventoryItem?.currency || currencyCode || inventoryItem?.price?.localCurrencies?.code || 'N/A'
      }
    });
  };

if(viewMode === "list"){
  return (
    <div>
      {pkgArr && pkgArr.length > 0 ? (
        pkgArr.map((pkg: any, idx: number) => {
          const isPackageInCart = isAuthenticated ? (pkg.isInCart || false) : isInCart(pkg.id);
          return (
            <Card
              key={pkg.id || idx}
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
            >
              <div className="flex flex-col md:flex-row items-stretch">
                {/* Image */}
                <div className="relative w-full md:w-64 h-40 flex-shrink-0 flex items-center justify-center bg-gray-50 rounded-lg md:ml-6 mt-4 md:mt-6 mb-4 md:mb-6">
                  <img
                    src={pkg.thumbnail || '/nomedia.jpg'}
                    alt={pkg.name}
                    className="w-full h-full object-cover rounded-lg"
                  />
                  {/* <Badge className={`absolute top-2 left-4 z-10 ${pkg.numberOfPurchase < pkg.purchaseLimitation ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                    {pkg.numberOfPurchase < pkg.purchaseLimitation ? 'Available' : 'Sold Out'}
                  </Badge> */}
                   {isPackageInCart && (
                      <Badge className="absolute top-2 left-4 bg-emerald-500 text-white">
                        In Cart
                      </Badge>
                    )}
                  <Badge className="absolute top-2 right-4 z-10 bg-emerald-500 text-white">
                    {pkg.inventory?.total || 0} Locations
                  </Badge>
                </div>
                {/* Details */}
                <div className="flex-1 w-full p-4 md:p-6 flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2 break-words">{pkg.name || pkg.title}</h3>
                    {/* Locations */}
                    <div className="mb-3">
                      {Array.isArray(pkg.inventories) && pkg.inventories.length > 0 ? (
                        <div className="space-y-1">
                          {pkg.inventories.slice(0, 3).map((billboard: any, index: number) => {
                            const fullLocationName = `${billboard.name}${billboard.location ? `, ${billboard.location}` : ''}`;
                            return (
                              <div key={index} className="flex items-center text-sm text-gray-600">
                                <MapPin className="mr-2 h-3 w-3 text-emerald-500 flex-shrink-0" />
                                <span
                                  className="truncate max-w-full md:max-w-[350px] lg:max-w-[550px] inline-block align-middle cursor-pointer"
                                  title={fullLocationName}
                                  style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                                >
                                  {fullLocationName}
                                </span>
                              </div>
                            );
                          })}
                          {pkg.inventories.length > 3 && (
                            <div className="flex items-center text-sm text-gray-500">
                              <MapPin className="mr-2 h-3 w-3 text-gray-400 flex-shrink-0" />
                              <span
                                className="truncate max-w-full md:max-w-[350px] lg:max-w-[550px] inline-block align-middle cursor-pointer"
                                title={pkg.inventories.slice(3).map((b: any) => `${b.name}${b.location ? `, ${b.location}` : ''}`).join('; ')}
                                style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                              >
                                +{pkg.inventories.length - 3} more locations
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="mr-2 h-3 w-3 text-gray-400 flex-shrink-0" />
                          <span>No locations available</span>
                        </div>
                      )}
                    </div>
                    {/* Info rows */}
                    <div className="text-sm text-gray-600 mb-1 flex flex-col md:flex-row md:justify-between md:items-center">
                      <span className="text-sm text-gray-500">Package Validity:</span>
                      <span>
                        {safeFormatDate(pkg.startDate ? new Date(pkg.startDate) : null, 'dd MMM yyyy', '-')} - {safeFormatDate(pkg.endDate ? new Date(pkg.endDate) : null, 'dd MMM yyyy', '-')}
                        {/* {safeFormatDate(pkg.validityStartDate ? new Date(pkg.validityStartDate) : null, 'dd MMM yyyy', '-')} - {safeFormatDate(pkg.validityEndDate ? new Date(pkg.validityEndDate) : null, 'dd MMM yyyy', '-')} */}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-1 flex flex-col md:flex-row md:justify-between md:items-center">
                      <span className="text-sm text-gray-500">Discount:</span>
                      {pkg.discount ? (
                        <span className="text-emerald-600 font-semibold">{pkg.discount}% off</span>
                      ) : (<span>-</span>)}
                    </div>
                    <div className="text-sm text-gray-600 mb-1 flex flex-col md:flex-row md:justify-between md:items-center">
                      <span className="text-sm text-gray-500">Minimum Duration:</span>
                      <span>{pkg.campaignDuration || 'N/A'} days</span>
                    </div>
                    <div className="text-sm text-gray-600 mb-1 flex flex-col md:flex-row md:justify-between md:items-center">
                      <span className="text-sm text-gray-500">Package Period:</span>
                      <span>
                        {safeFormatDate(pkg.validityStartDate ? new Date(pkg.validityStartDate) : null, 'dd MMM yyyy', '-')} - {safeFormatDate(pkg.validityEndDate ? new Date(pkg.validityEndDate) : null, 'dd MMM yyyy', '-')}
                        {/* {safeFormatDate(pkg.startDate ? new Date(pkg.startDate) : null, 'dd MMM yyyy', '-')} - {safeFormatDate(pkg.endDate ? new Date(pkg.endDate) : null, 'dd MMM yyyy', '-')} */}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-1 flex flex-col md:flex-row md:justify-between md:items-center">
                      <span className="text-sm text-gray-500">Standard Price:</span>
                      <span className="text-sm text-gray-500 line-through">{pkg?.currency || currencyCode} {pkg?.price?.toLocaleString()}</span>
                    </div>
                    {pkg.originalPrice && pkg.discount && (
                      <div className="text-sm text-gray-400 line-through mb-1">
                        {pkg?.currency || currencyCode} {Number(pkg.originalPrice).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                    )}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between border-t pt-2">
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-500">Our Offer:</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-3 w-3 text-gray-400" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-left bg-gray-900 text-white p-3 rounded-md shadow-lg border border-gray-700">
                              <div className="space-y-1">
                                <p className="font-medium text-white">Fixed Package Pricing</p>
                                <p className="text-gray-200">• Bundle of multiple inventory locations</p>
                                <p className="text-gray-200">• Fixed price regardless of campaign duration</p>
                                <p className="text-gray-200">• All package locations included in price</p>
                                <p className="text-gray-200">• No spot-level pricing calculations</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <span className="text-lg font-bold text-black-600 text-emerald-600">{pkg?.currency || currencyCode} {pkg?.totalPrice?.toLocaleString()}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-4 items-center">
                      <div className="col-span-1 md:col-span-6">
                        <Button
                          variant="outline"
                          size="lg"
                          onClick={() => handleViewDetails(pkg)}
                          className="w-full border-black-600 text-black-600"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Details
                        </Button>
                      </div>
                      <div className="col-span-1 md:col-span-6 md:mt-0">
                        <Button
                          size="lg"
                          onClick={isPackageInCart ? () => handleRemoveFromCart(pkg) : () => handleAddToCart(pkg)}
                          disabled={pkg.numberOfPurchase >= pkg.purchaseLimitation || !isPackageAvailable(pkg)}
                          className={`w-full ${isPackageInCart
                            ? "bg-red-600 hover:bg-red-700 text-white"
                            : "bg-emerald-600 hover:bg-emerald-700 text-white"
                          }`}
                        >
                          {pkg.numberOfPurchase >= pkg.purchaseLimitation ? 'Sold Out' : !isPackageAvailable(pkg) ? 'UnAvailable' : addCartLoading ? 'Adding...' : isPackageInCart ? 'Remove' : 'Add to Cart'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          );
        })
      ) : (
        <div className="text-center text-gray-500">No packages available.</div>
      )}
    </div>
  );
} else {
  return (
    <div className="mx-2">
      {pkgArr.map((pkg: any, idx: number) => {
        // Debug log for each package
        console.log(`[PackageCard] pkg[${idx}]:`, pkg);
        // For authenticated users, use the pkg.isInCart from API response
        // For non-authenticated users, use local cart context
        const isPackageInCart = isAuthenticated ? (pkg.isInCart || false) : isInCart(pkg.id);
        
        console.log(`[PackageCard] Package ${pkg.id} - isAuthenticated: ${isAuthenticated}, pkg.isInCart: ${pkg.isInCart}, local isInCart: ${isInCart(pkg.id)}, final isPackageInCart: ${isPackageInCart}`);
        
        return (
          <Card key={pkg.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden m-0 w-auto">
            <div className="relative">
              {/* <Badge className={`absolute top-3 left-3 z-10 ${pkg.numberOfPurchase < pkg.purchaseLimitation ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                {pkg.numberOfPurchase < pkg.purchaseLimitation ? 'Available' : 'Sold Out'}
              </Badge> */}
              {isPackageInCart && (
                <Badge className="absolute top-3 left-3 z-10 bg-emerald-600 text-white">
                  In Cart
                </Badge>
              )}
              <Badge className="absolute top-3 right-3 z-10 bg-emerald-500 text-white">
                {pkg.inventory?.total || 0} Locations
              </Badge>
              <img
                src={pkg.thumbnail || '/nomedia.jpg'}
                alt={pkg.name}
                className="w-full h-48 object-cover"
              />
            </div>
            <CardContent className="p-4">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 cursor-pointer">{pkg.name}</h3>
                {/* Package Locations List */}
                <div className="mb-3 h-20">
                  {Array.isArray(pkg.inventories) && pkg.inventories.length > 0 ? (
                    <div className="space-y-1">
                      {pkg.inventories.slice(0, 3).map((billboard: any, index: number) => {
                        const fullLocationName = `${billboard.name}${billboard.location ? `, ${billboard.location}` : ''}`;
                        return (
                          <div key={index} className="flex items-center text-sm text-gray-600">
                            <MapPin className="mr-2 h-3 w-3 text-emerald-500 flex-shrink-0" />
                            <span className="truncate" title={fullLocationName}>{fullLocationName}</span>
                          </div>
                        );
                      })}
                      {pkg.inventories.length > 3 && (
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin className="mr-2 h-3 w-3 text-gray-400 flex-shrink-0" />
                          <span 
                            className="truncate" 
                            title={`Additional locations: ${pkg.inventories.slice(3).map((b: any) => `${b.name}${b.location ? `, ${b.location}` : ''}`).join('; ')}`}
                          >
                            +{pkg.inventories.length - 3} more locations
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="mr-2 h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span>No locations available</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Package Validity:</span>
                    <span className="text-sm font-medium text-gray-700">
                      {safeFormatDate(pkg.startDate ? new Date(pkg.startDate) : null, 'dd MMM yyyy', '-')} - {safeFormatDate(pkg.endDate ? new Date(pkg.endDate) : null, 'dd MMM yyyy', '-')}
                      {/* {safeFormatDate(pkg.validityStartDate ? new Date(pkg.validityStartDate) : null, 'dd MMM yyyy', '-')} - {safeFormatDate(pkg.validityEndDate ? new Date(pkg.validityEndDate) : null, 'dd MMM yyyy', '-')} */}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Discount:</span>
                    <span className="text-sm font-medium text-emerald-600">{pkg.discount || 0}% off</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Minimum Duration:</span>
                    <span className="text-sm font-medium text-emerald-600">{pkg.campaignDuration || 'N/A'} days</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Package Period:</span>
                    <span className="text-sm font-medium text-gray-700">
                        {safeFormatDate(pkg.validityStartDate ? new Date(pkg.validityStartDate) : null, 'dd MMM yyyy', '-')} - {safeFormatDate(pkg.validityEndDate ? new Date(pkg.validityEndDate) : null, 'dd MMM yyyy', '-')}
                        {/* {safeFormatDate(pkg.startDate ? new Date(pkg.startDate) : null, 'dd MMM yyyy', '-')} - {safeFormatDate(pkg.endDate ? new Date(pkg.endDate) : null, 'dd MMM yyyy', '-')} */}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Standard Price:</span>
                    <span className="text-sm text-gray-500 line-through">{pkg?.currency || currencyCode} {pkg?.price?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-2">
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-500">Our Offer:</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-3 w-3 text-gray-400" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-left bg-gray-900 text-white p-3 rounded-md shadow-lg border border-gray-700">
                            <div className="space-y-1">
                              <p className="font-medium text-white">Fixed Package Pricing</p>
                              <p className="text-gray-200">• Bundle of multiple inventory locations</p>
                              <p className="text-gray-200">• Fixed price regardless of campaign duration</p>
                              <p className="text-gray-200">• All package locations included in price</p>
                              <p className="text-gray-200">• No spot-level pricing calculations</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <span className="text-lg font-bold text-black-600 text-emerald-600">{pkg?.currency || currencyCode} {pkg?.totalPrice?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              {/* Action buttons */}
              <div className="flex gap-2 justify-center mt-2">
                {/* View Details Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewDetails(pkg)}
                  className="border-black-600 text-black-600"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View Details
                </Button>
                
              {/* Add to Cart / Remove from Cart Button */}
                <Button
                  size="sm"
                  onClick={isPackageInCart ? () => handleRemoveFromCart(pkg) : () => handleAddToCart(pkg)}
                  disabled={pkg.numberOfPurchase >= pkg.purchaseLimitation || !isPackageAvailable(pkg)}
                  className={`${isPackageInCart
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
                    }`}
                >
                  {pkg.numberOfPurchase >= pkg.purchaseLimitation ? 'Sold Out' : !isPackageAvailable(pkg) ? 'UnAvailable' : addCartLoading ? 'Adding...' : isPackageInCart ? 'Remove' : 'Add to Cart'}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
}